import { WikiMarkdown } from '@/components/wiki/WikiMarkdown';
import { ApiError, api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { Archive, ExternalLink, FileText, Pencil, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

interface EditDraft {
  title: string;
  takeaway: string;
  body: string;
  categorySlug: string;
}

interface Props {
  slug: string;
}

export function WikiPageView({ slug }: Props) {
  const pageQuery = useQuery({
    queryKey: ['wiki', 'page', slug],
    queryFn: () => api.wikiPage(slug),
    retry: (failureCount, err) => {
      if (err instanceof ApiError && err.status === 404) return false;
      return failureCount < 2;
    },
  });

  const similarQuery = useQuery({
    queryKey: ['wiki', 'similar', slug],
    queryFn: () => api.wikiSimilarPages(slug, 5),
    enabled: pageQuery.isSuccess,
    staleTime: 60_000,
    retry: false,
  });

  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const archiveMutation = useMutation({
    mutationFn: (reason: string) => api.adminWikiPageDelete(slug, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wiki'] });
      toast.success('페이지를 보관했습니다', {
        description: '검색/목차에서 숨겨지며, 관리자 화면에서 복원할 수 있습니다.',
      });
      navigate({ to: '/wikis' });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : '보관에 실패했습니다.');
    },
  });

  // 편집 모달 (admin 전용). 카테고리 목록은 admin 일 때만 로드.
  const categoriesQuery = useQuery({
    queryKey: ['admin', 'wiki', 'categories'],
    queryFn: api.adminWikiCategoriesList,
    enabled: isAdmin,
  });
  const categories = categoriesQuery.data?.categories ?? [];
  const [draft, setDraft] = useState<EditDraft | null>(null);
  const updateMutation = useMutation({
    mutationFn: (d: EditDraft) =>
      api.adminWikiPageUpdate(slug, {
        title: d.title,
        takeaway: d.takeaway,
        body: d.body,
        categorySlug: d.categorySlug.length > 0 ? d.categorySlug : null,
      }),
    onSuccess: () => {
      // 본문 덮어쓰기 완료 — 서버가 리비전/감사 로그 기록 + 임베딩(RAG) 재계산 큐잉.
      queryClient.invalidateQueries({ queryKey: ['wiki'] });
      setDraft(null);
      toast.success('페이지를 저장했습니다', {
        description: '검색 색인(RAG)이 백그라운드에서 재계산됩니다.',
      });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : '저장에 실패했습니다.');
    },
  });

  if (pageQuery.isLoading) {
    return <div className="p-8 text-foreground-muted text-sm">로딩 중…</div>;
  }

  if (pageQuery.isError) {
    const err = pageQuery.error;
    if (err instanceof ApiError && err.status === 404) {
      return (
        <div className="mx-auto max-w-2xl px-6 py-12 text-center">
          <h1 className="mb-3 text-2xl font-semibold">페이지를 찾을 수 없습니다</h1>
          <p className="mb-6 text-foreground-muted">
            <code className="rounded bg-code-bg px-2 py-1">{slug}</code> 슬러그의 위키 페이지가
            존재하지 않거나 삭제되었습니다.
          </p>
          <Link to="/wikis" className="text-accent underline">
            위키 목차로
          </Link>
        </div>
      );
    }
    return (
      <div className="p-8 text-destructive text-sm">
        페이지를 불러오지 못했습니다: {(err as Error).message}
      </div>
    );
  }

  const page = pageQuery.data;
  if (!page) return null;

  // outlinks 의 resolved 정보로 끊긴 링크 표시.
  const resolved = new Set(page.outlinks.filter((o) => o.resolved).map((o) => o.slug));
  // ?from=chat&chatId=… 으로 진입했으면 본문 내부 [[slug]] 클릭 시에도 이어서 유지.
  const chatParams =
    typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
  const fromChat = chatParams?.get('from') === 'chat';
  const fromChatId = chatParams?.get('chatId') ?? null;
  const chatReturnQs = fromChat
    ? `from=chat${fromChatId ? `&chatId=${encodeURIComponent(fromChatId)}` : ''}`
    : '';

  return (
    <div className="mx-auto max-w-3xl px-6 py-10">
      <header className="mb-6">
        {fromChat ? null : page.categorySlug ? (
          <Link
            to="/wikis/c/$slug"
            params={{ slug: page.categorySlug }}
            className="mb-2 inline-block text-xs text-foreground-muted hover:text-accent"
          >
            ← {page.categorySlug}
          </Link>
        ) : (
          <Link
            to="/wikis"
            className="mb-2 inline-block text-xs text-foreground-muted hover:text-accent"
          >
            ← 위키 목차
          </Link>
        )}
        <h1 className="text-3xl font-semibold tracking-tight">{page.title}</h1>
        <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-foreground-faint">
          <code className="rounded bg-code-bg px-2 py-0.5">{page.slug}</code>
          <span>업데이트: {new Date(page.updatedAt).toLocaleString('ko-KR')}</span>
          {page.source === 'manual' ? (
            <span className="rounded bg-surface px-2 py-0.5 text-foreground-muted">수동 편집</span>
          ) : null}
          {isAdmin ? (
            <div className="ml-auto flex items-center gap-1">
              <button
                type="button"
                onClick={() =>
                  setDraft({
                    title: page.title,
                    takeaway: page.takeaway,
                    body: page.body,
                    categorySlug: page.categorySlug ?? '',
                  })
                }
                className="inline-flex items-center gap-1 rounded border border-border px-2 py-0.5 text-foreground-muted hover:border-accent hover:text-accent"
                title="이 페이지 본문 수정"
              >
                <Pencil className="size-3.5" />
                수정
              </button>
              <button
                type="button"
                onClick={() => {
                  if (window.confirm(`"${page.title}" 페이지를 보관할까요? (복원 가능)`)) {
                    archiveMutation.mutate('관리자 보관');
                  }
                }}
                disabled={archiveMutation.isPending}
                className="inline-flex items-center gap-1 rounded border border-border px-2 py-0.5 text-foreground-muted hover:border-destructive hover:text-destructive disabled:opacity-50"
                title="이 페이지를 보관 (복원 가능)"
              >
                <Archive className="size-3.5" />
                {archiveMutation.isPending ? '보관 중…' : '보관'}
              </button>
            </div>
          ) : null}
        </div>
      </header>

      {page.sourceDocuments.length > 0 ? (
        <section className="mb-6 rounded-md border border-border bg-surface px-4 py-3">
          <div className="mb-2 text-xs font-semibold text-foreground-muted">
            원문 {page.sourceDocuments.length > 1 ? `(${page.sourceDocuments.length}개)` : ''}
          </div>
          <ul className="space-y-1.5">
            {page.sourceDocuments.map((d) => (
              <li key={d.id} className="flex items-center gap-2 text-sm">
                <FileText className="size-3.5 shrink-0 text-foreground-faint" />
                {d.externalUrl ? (
                  <a
                    href={d.externalUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-accent hover:underline truncate"
                  >
                    <span className="truncate">{d.originalName}</span>
                    <ExternalLink className="size-3 shrink-0" />
                  </a>
                ) : (
                  <span className="text-foreground truncate">{d.originalName}</span>
                )}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <article className="prose-wiki">
        <WikiMarkdown
          takeaway={page.takeaway}
          body={page.body}
          resolvedSlugs={resolved}
          linkSearchParam={chatReturnQs || undefined}
        />
      </article>

      {page.backlinks.length > 0 ? (
        <section className="mt-10 border-t border-border pt-6">
          <h2 className="mb-3 text-sm font-semibold text-foreground-muted">
            이 페이지를 참조하는 페이지
          </h2>
          <ul className="space-y-1">
            {page.backlinks.map((b) => {
              const href: string = `/wikis/p/${encodeURIComponent(b.slug)}${chatReturnQs ? `?${chatReturnQs}` : ''}`;
              return (
                <li key={b.slug}>
                  <Link to={href} className="text-accent hover:underline">
                    {b.title}
                  </Link>
                  <span className="ml-2 text-xs text-foreground-faint">
                    <code className="rounded bg-code-bg px-1">{b.slug}</code>
                  </span>
                </li>
              );
            })}
          </ul>
        </section>
      ) : null}

      {similarQuery.data?.enabled && similarQuery.data.results.length > 0 ? (
        <section className="mt-10 border-t border-border pt-6">
          <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-foreground-muted">
            <Sparkles className="size-4 text-accent" />
            의미적으로 관련된 페이지
            <span className="text-xs font-normal text-foreground-faint">
              (임베딩 cosine 가까운 순)
            </span>
          </h2>
          <ul className="space-y-2">
            {similarQuery.data.results.map((s) => (
              <li
                key={s.slug}
                className="rounded-md border border-border bg-surface p-3 hover:border-accent transition-colors"
              >
                <Link
                  to={
                    `/wikis/p/${encodeURIComponent(s.slug)}${chatReturnQs ? `?${chatReturnQs}` : ''}` as string
                  }
                  className="block"
                >
                  <div className="flex items-baseline justify-between gap-2">
                    <div className="font-medium text-foreground">{s.title}</div>
                    <span className="shrink-0 text-[10px] text-foreground-faint">
                      유사도 {(1 - s.distance).toFixed(2)}
                    </span>
                  </div>
                  <div className="mt-0.5 text-xs text-foreground-muted line-clamp-2">
                    {s.takeaway}
                  </div>
                  <code className="mt-0.5 inline-block rounded bg-code-bg px-1 py-0.5 text-[10px] text-foreground-faint">
                    {s.slug}
                  </code>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {isAdmin && draft ? (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-3xl max-h-[90vh] overflow-auto rounded-md border border-border bg-background p-5 shadow-xl">
            <div className="mb-3 flex items-baseline justify-between">
              <h2 className="text-lg font-semibold">
                페이지 편집 — <code className="font-mono text-sm">{page.slug}</code>
              </h2>
              <button
                type="button"
                onClick={() => setDraft(null)}
                className="text-sm text-foreground-muted hover:text-foreground"
              >
                닫기
              </button>
            </div>
            <p className="mb-3 text-xs text-foreground-faint">
              저장하면 기존 본문을 덮어쓰고, 변경 이력(리비전·감사 로그)이 남으며 검색 색인(RAG)이
              자동으로 재계산됩니다.
            </p>
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-xs font-medium" htmlFor="page-view-edit-title">
                  제목
                </label>
                <input
                  id="page-view-edit-title"
                  value={draft.title}
                  onChange={(e) => setDraft({ ...draft, title: e.currentTarget.value })}
                  className="w-full rounded border border-border bg-surface px-3 py-2 text-sm focus:border-accent focus:outline-none"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium" htmlFor="page-view-edit-category">
                  카테고리
                </label>
                <select
                  id="page-view-edit-category"
                  value={draft.categorySlug}
                  onChange={(e) => setDraft({ ...draft, categorySlug: e.currentTarget.value })}
                  className="w-full rounded border border-border bg-surface px-3 py-2 text-sm focus:border-accent focus:outline-none"
                >
                  <option value="">(분류 없음)</option>
                  {categories.map((c) => (
                    <option key={c.slug} value={c.slug}>
                      {c.name} ({c.slug})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium" htmlFor="page-view-edit-takeaway">
                  takeaway (1~3 문장 핵심)
                </label>
                <textarea
                  id="page-view-edit-takeaway"
                  rows={3}
                  value={draft.takeaway}
                  onChange={(e) => setDraft({ ...draft, takeaway: e.currentTarget.value })}
                  className="w-full rounded border border-border bg-surface px-3 py-2 text-sm focus:border-accent focus:outline-none"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium" htmlFor="page-view-edit-body">
                  본문 (마크다운)
                </label>
                <textarea
                  id="page-view-edit-body"
                  rows={20}
                  value={draft.body}
                  onChange={(e) => setDraft({ ...draft, body: e.currentTarget.value })}
                  className="w-full rounded border border-border bg-surface px-3 py-2 text-sm font-mono leading-6 focus:border-accent focus:outline-none"
                />
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => updateMutation.mutate(draft)}
                  disabled={updateMutation.isPending}
                  className="rounded bg-accent px-4 py-2 text-sm font-medium text-accent-foreground disabled:opacity-50"
                >
                  {updateMutation.isPending ? '저장 중…' : '저장 (덮어쓰기)'}
                </button>
                <button
                  type="button"
                  onClick={() => setDraft(null)}
                  className="rounded border border-border px-4 py-2 text-sm"
                >
                  취소
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
